/**
 * 翡翠绿设计系统 - 统一主题配置
 * 基于Ant Design 5.x Design Token系统
 * 参考登录页面的翡翠绿配色方案
 */

import type { ThemeConfig } from 'antd';
import { theme } from 'antd';

// 翡翠绿色彩系统
export const emeraldColors = {
  // 主色调 - 翡翠绿渐变
  primary: {
    50: '#ecfdf5', // 最浅
    100: '#d1fae5', // 浅
    200: '#a7f3d0', //
    300: '#6ee7b7', //
    400: '#34d399', //
    500: '#10b981', // 主色 - 翡翠绿
    600: '#059669', // 深
    700: '#047857', // 更深
    800: '#065f46', //
    900: '#064e3b', // 最深
  },

  // 辅助色彩
  secondary: {
    emerald: '#50c878', // 祖母绿
    jade: '#00a86b', // 翡翠
    mint: '#98fb98', // 薄荷绿
    forest: '#228b22', // 森林绿
  },

  // 中性色彩
  neutral: {
    white: '#ffffff',
    gray50: '#f9fafb',
    gray100: '#f3f4f6',
    gray200: '#e5e7eb',
    gray300: '#d1d5db',
    gray400: '#9ca3af',
    gray500: '#6b7280',
    gray600: '#4b5563',
    gray700: '#374151',
    gray800: '#1f2937',
    gray900: '#111827',
  },

  // 功能色彩
  functional: {
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
};

// 渐变系统
export const emeraldGradients = {
  primary: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  soft: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
  hero: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
  glass:
    'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
  page: 'linear-gradient(135deg, rgb(248, 250, 252) 0%, rgb(255, 255, 255) 50%, rgba(236, 253, 245, 0.3) 100%)',
};

// 阴影系统
export const emeraldShadows = {
  sm: '0 1px 2px 0 rgba(16, 185, 129, 0.05)',
  md: '0 4px 6px -1px rgba(16, 185, 129, 0.1), 0 2px 4px -1px rgba(16, 185, 129, 0.06)',
  lg: '0 10px 15px -3px rgba(16, 185, 129, 0.1), 0 4px 6px -2px rgba(16, 185, 129, 0.05)',
  xl: '0 20px 25px -5px rgba(16, 185, 129, 0.1), 0 10px 10px -5px rgba(16, 185, 129, 0.04)',
  glass: '0 8px 32px rgba(16, 185, 129, 0.12)',
  glow: '0 0 20px rgba(16, 185, 129, 0.3)',
};

// 毛玻璃效果系统
export const glassmorphism = {
  primary: {
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: emeraldShadows.glass,
  },
  secondary: {
    background: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(16, 185, 129, 0.1)',
    boxShadow: emeraldShadows.md,
  },
  subtle: {
    background: 'rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(16, 185, 129, 0.05)',
    boxShadow: emeraldShadows.sm,
  },
};

// Ant Design 主题配置
export const emeraldTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    // 主色调
    colorPrimary: emeraldColors.primary[500],
    colorSuccess: emeraldColors.functional.success,
    colorWarning: emeraldColors.functional.warning,
    colorError: emeraldColors.functional.error,
    colorInfo: emeraldColors.functional.info,

    // 背景色
    colorBgContainer: emeraldColors.neutral.white,
    colorBgElevated: emeraldColors.neutral.white,
    colorBgLayout: emeraldColors.neutral.gray50,
    colorBgSpotlight: emeraldColors.primary[50],

    // 文字色
    colorText: emeraldColors.neutral.gray800,
    colorTextSecondary: emeraldColors.neutral.gray600,
    colorTextTertiary: emeraldColors.neutral.gray500,
    colorTextQuaternary: emeraldColors.neutral.gray400,

    // 边框色
    colorBorder: emeraldColors.neutral.gray200,
    colorBorderSecondary: emeraldColors.neutral.gray100,

    // 链接色
    colorLink: emeraldColors.primary[600],
    colorLinkHover: emeraldColors.primary[700],
    colorLinkActive: emeraldColors.primary[800],

    // 圆角
    borderRadius: 12,
    borderRadiusLG: 16,
    borderRadiusSM: 8,
    borderRadiusXS: 6,

    // 字体
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeXL: 20,
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,

    // 间距
    padding: 16,
    paddingLG: 24,
    paddingXL: 32,
    margin: 16,
    marginLG: 24,
    marginXL: 32,

    // 控制高度
    controlHeight: 36,
    controlHeightLG: 44,
    controlHeightSM: 28,

    // 线条宽度
    lineWidth: 1,
    lineWidthBold: 2,

    // 动画
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',

    // 阴影
    boxShadow: emeraldShadows.md,
    boxShadowSecondary: emeraldShadows.sm,
    boxShadowTertiary: emeraldShadows.lg,
  },

  components: {
    // 按钮组件
    Button: {
      colorPrimary: emeraldColors.primary[500],
      colorPrimaryHover: emeraldColors.primary[400],
      colorPrimaryActive: emeraldColors.primary[600],
      borderRadius: 8,
      controlHeight: 40,
      fontWeight: 500,
      boxShadow: emeraldShadows.sm,
    },

    // 菜单组件
    Menu: {
      colorBgContainer: 'rgba(255, 255, 255, 0.8)',
      itemBg: 'transparent',
      itemSelectedBg: emeraldColors.primary[50],
      itemHoverBg: 'rgba(16, 185, 129, 0.05)',
      itemColor: emeraldColors.neutral.gray700,
      itemSelectedColor: emeraldColors.primary[700],
      itemHoverColor: emeraldColors.primary[600],
      borderRadius: 8,
      itemMarginInline: 4,
    },

    // 卡片组件
    Card: {
      colorBgContainer: glassmorphism.primary.background,
      borderRadius: 16,
      boxShadow: emeraldShadows.glass,
      paddingLG: 24,
    },

    // 布局组件
    Layout: {
      headerBg: emeraldColors.neutral.white,
      bodyBg: emeraldColors.neutral.gray50,
      triggerBg: emeraldColors.primary[500],
    },

    // 输入框组件 - 使用正确的Component Token
    Input: {
      colorPrimary: emeraldColors.primary[500],
      colorBorder: emeraldColors.primary[300], // 默认边框颜色
      hoverBorderColor: emeraldColors.primary[400], // 悬停边框颜色
      activeShadow: `0 0 0 2px ${emeraldColors.primary[200]}`,
      colorBgContainer: emeraldColors.neutral.white,
      borderRadius: 8,
      controlHeight: 40,
      paddingInline: 16,
      algorithm: true, // 启用算法自动计算相关颜色
    },

    // 选择器组件 - 使用正确的Component Token
    Select: {
      colorPrimary: emeraldColors.primary[500],
      colorBorder: emeraldColors.primary[300], // 默认边框颜色
      optionSelectedBg: emeraldColors.primary[100],
      optionActiveBg: emeraldColors.primary[50],
      colorBgContainer: emeraldColors.neutral.white,
      borderRadius: 8,
      controlHeight: 40,
      algorithm: true, // 启用算法自动计算相关颜色
    },

    // 数字输入框 - 使用正确的Component Token
    InputNumber: {
      colorPrimary: emeraldColors.primary[500],
      colorBorder: emeraldColors.primary[300], // 默认边框颜色
      hoverBorderColor: emeraldColors.primary[400], // 悬停边框颜色
      activeShadow: `0 0 0 2px ${emeraldColors.primary[200]}`,
      colorBgContainer: emeraldColors.neutral.white,
      borderRadius: 8,
      controlHeight: 40,
      algorithm: true, // 启用算法自动计算相关颜色
    },

    // 日期选择器 - 使用正确的Component Token
    DatePicker: {
      colorPrimary: emeraldColors.primary[500],
      colorBorder: emeraldColors.primary[300], // 默认边框颜色
      activeShadow: `0 0 0 2px ${emeraldColors.primary[200]}`,
      colorBgContainer: emeraldColors.neutral.white,
      borderRadius: 8,
      controlHeight: 40,
      algorithm: true, // 启用算法自动计算相关颜色
    },

    // 表单组件
    Form: {
      labelColor: emeraldColors.neutral.gray700,
      labelFontSize: 14,
      labelRequiredMarkColor: emeraldColors.functional.error,
      itemMarginBottom: 16,
    },

    // 复选框 - 完整配置
    Checkbox: {
      colorPrimary: emeraldColors.primary[500],
      colorPrimaryHover: emeraldColors.primary[400], // 悬停颜色
      algorithm: true, // 启用算法自动计算
    },

    // 单选框 - 完整配置
    Radio: {
      colorPrimary: emeraldColors.primary[500],
      buttonCheckedBg: emeraldColors.primary[500], // 选中按钮背景
      buttonBg: emeraldColors.neutral.white, // 默认按钮背景
      buttonColor: emeraldColors.neutral.gray700, // 默认按钮文字颜色
      buttonSolidCheckedColor: emeraldColors.neutral.white, // 实心选中文字颜色
      dotSize: 8, // 圆点大小
      radioSize: 16, // 单选框大小
      algorithm: true, // 启用算法自动计算
    },

    // 开关
    Switch: {
      colorPrimary: emeraldColors.primary[500],
    },

    // 滑块
    Slider: {
      colorPrimary: emeraldColors.primary[500],
    },

    // 表格组件
    Table: {
      colorBgContainer: glassmorphism.primary.background,
      borderRadius: 12,
    },

    // 标签页组件
    Tabs: {
      colorBgContainer: 'transparent',
      colorText: emeraldColors.neutral.gray600,
    },

    // 步骤条组件
    Steps: {
      colorPrimary: emeraldColors.primary[500],
      colorText: emeraldColors.neutral.gray600,
      colorTextDescription: emeraldColors.neutral.gray500,
    },

    // 进度条组件
    Progress: {
      colorSuccess: emeraldColors.primary[500],
      borderRadius: 8,
    },

    // 标签组件
    Tag: {
      borderRadius: 6,
      fontSizeSM: 12,
      lineHeightSM: 1.5,
    },

    // 通知组件
    Notification: {
      colorBgElevated: glassmorphism.primary.background,
      borderRadius: 12,
      boxShadow: emeraldShadows.xl,
    },

    // 消息组件
    Message: {
      colorBgElevated: glassmorphism.primary.background,
      borderRadius: 8,
      boxShadow: emeraldShadows.lg,
    },
  },
};

// 暗色主题配置
export const emeraldDarkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...emeraldTheme.token,
    colorBgContainer: emeraldColors.neutral.gray800,
    colorBgElevated: emeraldColors.neutral.gray700,
    colorBgLayout: emeraldColors.neutral.gray900,
    colorText: emeraldColors.neutral.gray100,
    colorTextSecondary: emeraldColors.neutral.gray300,
  },
};

export default emeraldTheme;
